import React, { useCallback, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Flex, QueryLoader, Text, toast } from "@jsq/ds/core";
import { useComplianceCaseContext } from "../../entrypoints/ComplianceCaseContext";
import { BaseDocumentType } from "../../graphql/supergraphEnums";
import { useGetOwnersQuery } from "../../graphql/supergraphHooks";
import { type DocumentType } from "../../graphql/supergraphTypes";
import useMultiDocumentProcessor from "../../hooks/documents/useMultiDocumentProcessor";
import { DefaultModal } from "../common/DefaultModal";
import { sharedHeaderStyle, textColorNeutral800 } from "../common/style_vanilla.css";
import { DocumentData } from "../documents/DocumentCollector/types";
import { MultiDocumentCollector } from "../documents/MultiDocumentCollector";
import { provideLegalName } from "../ProfileTree/utils";
import { getExistingProfileDocumentsOrDefault } from "./utils";

export const JointLandingScreen = ({ onStepCompletion }: { onStepCompletion: () => void }) => {
  const history = useHistory();
  const { profileId } = useParams<{ profileId: string }>();
  const { caseId, basePath, skipDetailsForRootProfile } = useComplianceCaseContext();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showBackWarningModal, setShowBackWarningModal] = useState(false);

  const ownerResults = useGetOwnersQuery({
    variables: {
      caseId: caseId,
    },
    fetchPolicy: "no-cache",
    skip: !caseId,
  });

  const owners =
    ownerResults.data?.complianceCase?.investingProfile?.beneficialOwners.map(
      (owner) => owner.childProfile,
    ) || [];
  const documents = ownerResults.data?.complianceCase?.documents ?? [];
  const documentTypes =
    (
      ownerResults.data?.complianceCase?.investingProfile?.rulesEngine as {
        documentTypes: { baseType: BaseDocumentType }[];
      }
    )?.documentTypes
      .map((dt) => dt.baseType)
      .sort((a, b) => a.localeCompare(b)) ?? [];

  const documentCollections = owners.map((owner) => ({
    profileId: owner.id,
    title: provideLegalName(owner),
    documentDetails: getExistingProfileDocumentsOrDefault(
      documentTypes,
      owner.id,
      documents as DocumentType[],
    ),
  }));
  const { processDocuments, loading } = useMultiDocumentProcessor(caseId);

  /**
   * Handle unsaved changes updates from MultiDocumentCollector
   */
  const handleUnsavedChangesChange = useCallback((hasChanges: boolean) => {
    setHasUnsavedChanges(hasChanges);
  }, []);

  /**
   * Handle navigation back with unsaved changes check
   */
  const handleNavigateBack = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowBackWarningModal(true);
    } else {
      history.goBack();
    }
  }, [hasUnsavedChanges, history]);

  const handleDocumentProcessing = useCallback(
    async (newFiles: DocumentData[], deleteDocIds: number[]) => {
      try {
        await processDocuments(newFiles, deleteDocIds);
        const documentsUploaded = newFiles.some((document) => document.file != null);
        if (documentsUploaded || deleteDocIds.length > 0) {
          toast.success("Documents updated successfully");
        }

        setHasUnsavedChanges(false); // Clear unsaved changes after successful submission
        onStepCompletion();
      } catch (error) {
        toast.error("Failed to update documents");
      }
    },
    [
      processDocuments,
      onStepCompletion,
      basePath,
      profileId,
      history,
      skipDetailsForRootProfile,
    ],
  );

  return (
    <QueryLoader result={[ownerResults]}>
      <Flex flexDirection="column" gap="20px">
        <Text variant="h3" className={sharedHeaderStyle}>
          Identification
        </Text>
        <Text
          variant="regular-md"
          className={textColorNeutral800}
        >{`Due to regulatory requirements, the following documents are required.`}</Text>
        <MultiDocumentCollector
          caseId={caseId}
          documentCollections={documentCollections}
          onComplete={(newUploadFiles, deletedIds) => {
            void handleDocumentProcessing(newUploadFiles, deletedIds);
          }}
          onNavigateBack={handleNavigateBack}
          onUnsavedChangesChange={handleUnsavedChangesChange}
          isSubmitting={loading}
        />
      </Flex>
      <DefaultModal
        show={showBackWarningModal}
        title="Warning"
        body="You have unsaved changes. Are you sure you want to go back without submitting?"
        negBtnText="Cancel"
        posBtnText="Back"
        posBtnVariant="danger"
        onClose={() => setShowBackWarningModal(false)}
        onConfirm={() => {
          setShowBackWarningModal(false);
          setHasUnsavedChanges(false);
          history.goBack();
        }}
      />
    </QueryLoader>
  );
};
